import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { useRBAC } from "../../hooks/useRBAC";
import { useModuleAvailability } from "../../hooks/useModuleAvailability";
import { useShowAiIntelligence } from "../../contexts/FeatureFlagsContext";
import {
  fetchCoverage, refreshLinks, coverageText, formatDate, formatTime, asUtc,
  COVERAGE_SETUP_TARGETS, COVERAGE_REFRESH_BUTTON, COVERAGE_REFRESH_QUEUED, COVERAGE_REFRESH_OFF,
  COVERAGE_REFRESH_RECENT, COVERAGE_REFRESH_FAILED, COVERAGE_LAST_ATTEMPT, COVERAGE_REFRESH_COOLDOWN_MS,
  COVERAGE_PAGE_TITLE, COVERAGE_HEALTH_HEADING, COVERAGE_MEASURES_HEADING, COVERAGE_NOTE, COVERAGE_SYNC_OFF_NOTE,
  COVERAGE_LOAD_FAILED, COVERAGE_NO_TYPES, COVERAGE_CALCULATED, COVERAGE_CONNECTED_ON, COVERAGE_CONNECTED_OFF,
  COVERAGE_HEALTH_UNAVAILABLE, COVERAGE_MEASURES_UNAVAILABLE, COVERAGE_FORBIDDEN, COVERAGE_COLUMNS,
  COVERAGE_LOADING, COVERAGE_NEVER, COVERAGE_MEASURE_LABEL, COVERAGE_TYPE_LABEL,
} from "./fusionApi";

const count = (v) => Number(v || 0).toLocaleString();
// Dates show date and time (dry-run decision 11).
const when = (iso) => (iso ? `${formatDate(iso)} ${formatTime(iso)}`.trim() : COVERAGE_NEVER);

// Rows that a set-up step can fix (FUSION.md §1); the rest get an empty Next step cell.
const SETUP_STATES = ["NotConfigured", "Unavailable"];

/** The 409 RecentlySynced text: the attempt time and that time plus the cooldown (zone-less times are UTC). */
const recentText = (lastAttemptAt) => {
  const t = new Date(asUtc(lastAttemptAt));
  if (!lastAttemptAt || Number.isNaN(t.getTime())) return COVERAGE_REFRESH_FAILED;
  return COVERAGE_REFRESH_RECENT
    .replace("{time}", formatTime(t.toISOString()))
    .replace("{next}", formatTime(new Date(t.getTime() + COVERAGE_REFRESH_COOLDOWN_MS).toISOString()));
};

/** The coverage note (AC4): store not read for this org → COVERAGE_NOTE; read but sync off → COVERAGE_SYNC_OFF_NOTE. */
const noteFor = (r) => {
  if (!(r.storeReadGlobal && r.orgSwitch)) return COVERAGE_NOTE;
  if (!r.projectorsGlobal) return COVERAGE_SYNC_OFF_NOTE;
  return null;
};

const th = "text-left font-medium text-content-secondary pr-4 py-1";
const td = "pr-4 py-1";

/**
 * Fusion's Data coverage view (FUSION.md §1, §10.2): where links come from (source health per kind of
 * link) and how connected the data is (the x-of-y measures). Read-only; the API answers 403 to callers
 * without an unrestricted view of the organization. Rows that aren't set up get a "Next step" link to
 * the page that fixes them (COVERAGE_SETUP_TARGETS, gated like that page), and "Refresh links now"
 * queues a link sync (POST /fusion/coverage/refresh).
 *
 * Props:
 *  - refreshNonce: the panel's Refresh counter; a bump refetches.
 */
const FusionCoverageView = ({ refreshNonce }) => {
  const apiCall = useApi();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null); // null | "forbidden" | "failed"
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  // All hooks before the early returns below (rules-of-hooks lint is off in this repo).
  const navigate = useNavigate();
  const rbac = useRBAC();
  const showAi = useShowAiIntelligence();
  const { loaded: modulesLoaded, isModuleAvailable } = useModuleAvailability();

  /** The set-up target for a row, only when its state needs one and the caller passes the target page's guard. */
  const setupFor = (row) => {
    if (!SETUP_STATES.includes(row.state)) return null;
    const target = Object.prototype.hasOwnProperty.call(COVERAGE_SETUP_TARGETS, row.relationshipType)
      ? COVERAGE_SETUP_TARGETS[row.relationshipType] : null;
    if (!target) return null;
    const allowed = typeof rbac?.[target.can] === "function" && rbac[target.can]() === true;
    if (!allowed) return null;
    if (target.ai && !(showAi && modulesLoaded && isModuleAvailable("aiIntelligence"))) return null;
    return target;
  };

  const onRefresh = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      await refreshLinks(apiCall);
      setRefreshMsg(COVERAGE_REFRESH_QUEUED);
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;
      if (status === 409 && data?.reason === "ConnectedDataOff") setRefreshMsg(COVERAGE_REFRESH_OFF);
      else if (status === 409 && data?.reason === "RecentlySynced") setRefreshMsg(recentText(data.lastAttemptAt));
      else setRefreshMsg(COVERAGE_REFRESH_FAILED);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fetchCoverage(apiCall)
      .then((r) => { if (live) setReport(r || null); })
      .catch((e) => { if (live) { setReport(null); setError(e?.response?.status === 403 ? "forbidden" : "failed"); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [apiCall, refreshNonce, retry]);

  if (loading && !report) return (
    <div className="py-8 flex items-center justify-center gap-2 text-sm text-content-muted" role="status">
      <Loader2 className="h-4 w-4 animate-spin" /> {COVERAGE_LOADING}
    </div>
  );
  if (error === "forbidden") return <div className="py-8 text-center text-sm text-content-muted">{COVERAGE_FORBIDDEN}</div>;
  if (error || !report) return (
    <div className="py-8 text-center text-sm text-content-muted">
      {COVERAGE_LOAD_FAILED} <button className="text-indigo-600 hover:underline" onClick={() => setRetry((n) => n + 1)}>Try again</button>
    </div>
  );

  const note = noteFor(report);
  const health = Array.isArray(report.health) ? report.health : [];
  const measures = Array.isArray(report.measures) ? report.measures : [];

  return (
    <div className="text-sm text-content-secondary">
      <div className="flex items-baseline gap-2 flex-wrap mb-1">
        <h2 className="text-base font-semibold text-content-primary">{COVERAGE_PAGE_TITLE}</h2>
        {report.calculatedAt && <span className="text-xs text-content-muted">{COVERAGE_CALCULATED.replace("{time}", formatTime(report.calculatedAt))}</span>}
      </div>
      <div className="mb-2">{report.connectedData ? COVERAGE_CONNECTED_ON : COVERAGE_CONNECTED_OFF}</div>
      {note && <div className="mb-3 rounded-lg border border-border bg-theme-secondary px-3 py-2">{note}</div>}

      <section className="mb-4">
        <h3 className="font-semibold text-content-primary mb-1">{COVERAGE_HEALTH_HEADING}</h3>
        <div className="mb-2 flex items-center gap-3 flex-wrap text-xs">
          <span>{COVERAGE_LAST_ATTEMPT.replace("{when}", when(asUtc(report.lastSyncAttemptAt)))}</span>
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-indigo-600 hover:bg-theme-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!report.connectedData || refreshing}
            onClick={onRefresh}
          >
            {COVERAGE_REFRESH_BUTTON}
          </button>
          {!report.connectedData && !refreshMsg && <span>{COVERAGE_REFRESH_OFF}</span>}
          {refreshMsg && <span role="status">{refreshMsg}</span>}
        </div>
        {report.healthUnavailable ? (
          <p>{COVERAGE_HEALTH_UNAVAILABLE}</p>
        ) : health.length === 0 ? (
          <p>{COVERAGE_NO_TYPES}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr>{COVERAGE_COLUMNS.map((c) => <th key={c} scope="col" className={th}>{c}</th>)}</tr>
              </thead>
              <tbody>
                {health.map((r) => {
                  const setup = setupFor(r);
                  return (
                  <tr key={r.relationshipType} className="border-t border-border">
                    <td className={td}>{COVERAGE_TYPE_LABEL[r.relationshipType] || r.relationshipType}</td>
                    <td className={`${td} text-content-primary`}>{coverageText(r)}</td>
                    <td className={td}>{when(asUtc(r.lastCompleteAt))}</td>
                    <td className={`${td} text-right tabular-nums`}>{count(r.linksAtLastSync)}</td>
                    <td className={`${td} text-right tabular-nums`}>{count(r.failingFeeds)}</td>
                    <td className={`${td} text-right tabular-nums`}>{count(r.staleFeeds)}</td>
                    <td className={`${td} text-right tabular-nums`}>{count(r.crossRegionCount)}</td>
                    <td className={td}>
                      {setup && (
                        <button type="button" className="text-indigo-600 hover:underline whitespace-nowrap"
                          onClick={() => (setup.state ? navigate(setup.to, { state: setup.state }) : navigate(setup.to))}>
                          {setup.label}
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h3 className="font-semibold text-content-primary mb-1">{COVERAGE_MEASURES_HEADING}</h3>
        {report.measuresUnavailable ? (
          <p>{COVERAGE_MEASURES_UNAVAILABLE}</p>
        ) : (
          <table className="text-xs">
            <tbody>
              {measures.map((m) => {
                const d = Number(m.denominator || 0);
                const n = Number(m.numerator || 0);
                return (
                  <tr key={m.key}>
                    <td className={td}>{COVERAGE_MEASURE_LABEL[m.key] || m.key}</td>
                    <td className={`${td} text-right tabular-nums text-content-primary`}>{d === 0 ? "–" : `${n.toLocaleString()} of ${d.toLocaleString()}`}</td>
                    <td className={`${td} text-right tabular-nums`}>{d === 0 ? "–" : `${Math.round((100 * n) / d)}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default FusionCoverageView;
